import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiGetWordleWord, apiValidateWord } from '../utils/api';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import ShareModal from '../components/ShareModal';

const MAX_ATTEMPTS = 7;
const WORD_LENGTH  = 5;
const NUM_BOARDS   = 2;

const KEYBOARD_ROWS = [
  ['Q','W','E','R','T','Y','U','I','O','P'],
  ['A','S','D','F','G','H','J','K','L'],
  ['ENTER','Z','X','C','V','B','N','M','⌫'],
];

const FALLBACKS   = ['CRANE','STALE','SHARP','LIGHT','BRAVE','DREAM','GRAPE','PLUMB'];
const STATE_EMOJI = { correct: '🟩', present: '🟨', absent: '⬛' };

function buildShareText(guesses, status) {
  const score = status === 'won' ? guesses.length : 'X';
  const grid  = guesses.map(g =>
    g.states.map(bs => bs.map(s => STATE_EMOJI[s]).join('')).join(' ')
  ).join('\n');
  return `🟩🟩 WordZingo Dordle ${score}/${MAX_ATTEMPTS}\n\n${grid}\n\n🎯 Beat me! wordzingo.com/dordle`;
}

function getLetterStates(guess, target) {
  const result = Array(WORD_LENGTH).fill('absent');
  const tArr   = target.split('');
  const used   = Array(WORD_LENGTH).fill(false);
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guess[i] === tArr[i]) { result[i] = 'correct'; used[i] = true; }
  }
  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] === 'correct') continue;
    for (let j = 0; j < WORD_LENGTH; j++) {
      if (!used[j] && guess[i] === tArr[j]) { result[i] = 'present'; used[j] = true; break; }
    }
  }
  return result;
}

// Cell size: fills half the viewport minus container padding (24px), board gap (14px),
// and inner row gaps (4×4px = 16px), capped at 44px on large screens.
const RESPONSIVE_CELL = 'clamp(24px, calc(((100vw - 38px) / 2 - 16px) / 5), 44px)';

function MiniBoard({ boardIdx, target, guesses, current, boardWon, shake }) {
  const GAP = 4;
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="text-center mb-2">
        <span className="fw-bold" style={{
          background: boardWon ? '#dcfce7' : '#f1f5f9',
          color: boardWon ? '#065f46' : '#4b5563',
          padding: '0.18rem 0.75rem', borderRadius: 99, fontSize: '0.78rem'
        }}>
          {boardWon ? `✅ Word ${boardIdx + 1} Done!` : `Word ${boardIdx + 1}`}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: GAP }}>
        {Array.from({ length: MAX_ATTEMPTS }, (_, rowIdx) => {
          const guess   = guesses[rowIdx];
          const isActive = rowIdx === guesses.length && !boardWon;
          const letters  = guess
            ? guess.word.split('')
            : isActive ? current.split('') : [];
          return (
            <div key={rowIdx}
              className={isActive && shake ? 'shake' : ''}
              style={{ display: 'flex', gap: GAP, justifyContent: 'center' }}>
              {Array.from({ length: WORD_LENGTH }, (_, colIdx) => {
                const letter = letters[colIdx] || '';
                let cls = '';
                if (guess) cls = guess.states[boardIdx][colIdx];
                else if (letter && isActive) cls = 'filled';
                return (
                  <div key={colIdx} className={`wordle-cell ${cls}`}
                    style={{
                      width: RESPONSIVE_CELL,
                      height: RESPONSIVE_CELL,
                      fontSize: 'clamp(0.65rem, 3vw, 1rem)',
                      flexShrink: 0,
                    }}>
                    {letter}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dordle() {
  const [targets, setTargets]     = useState([]);
  const [guesses, setGuesses]     = useState([]);
  const [current, setCurrent]     = useState('');
  const [boardWon, setBoardWon]   = useState([false, false]);
  const [status, setStatus]       = useState('playing');
  const [keyStates, setKeyStates] = useState({});
  const [shake, setShake]         = useState(false);
  const [message, setMessage]     = useState('');
  const [loading, setLoading]     = useState(true);
  const [showShare, setShowShare] = useState(false);

  async function fetchWords() {
    setLoading(true);
    try {
      const results = await Promise.all(Array(NUM_BOARDS).fill(null).map(() => apiGetWordleWord()));
      const words = results.map(r => r.data.word.toUpperCase());
      const unique = [...new Set(words)];
      let fi = 0;
      while (unique.length < NUM_BOARDS) unique.push(FALLBACKS[fi++ % FALLBACKS.length]);
      setTargets(unique.slice(0, NUM_BOARDS));
    } catch {
      setTargets(FALLBACKS.slice(0, NUM_BOARDS));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchWords(); }, []);

  function newGame() {
    setGuesses([]); setCurrent(''); setStatus('playing');
    setBoardWon([false, false]); setKeyStates({}); setMessage(''); setShowShare(false);
    fetchWords();
  }

  function showMsg(msg, dur = 2500) {
    setMessage(msg);
    setTimeout(() => setMessage(''), dur);
  }

  const handleKey = useCallback(async (key) => {
    if (status !== 'playing') return;
    if (key === 'ENTER') {
      if (current.length < WORD_LENGTH) {
        setShake(true); setTimeout(() => setShake(false), 400);
        showMsg('Not enough letters!'); return;
      }
      try {
        const { data } = await apiValidateWord(current.toLowerCase());
        if (!data.valid) {
          setShake(true); setTimeout(() => setShake(false), 400);
          showMsg('Not in word list!'); return;
        }
      } catch { /* allow if API down */ }

      const newStates  = targets.map(t => getLetterStates(current, t));
      const newGuess   = { word: current, states: newStates };
      const newGuesses = [...guesses, newGuess];
      setGuesses(newGuesses);

      const newBoardWon = targets.map((t, i) => boardWon[i] || current === t);
      setBoardWon(newBoardWon);

      const ks = { ...keyStates };
      current.split('').forEach((ch, i) => {
        const prev  = ks[ch];
        const best  = newStates.reduce((acc, s) => {
          const v = s[i];
          if (acc === 'correct') return 'correct';
          if (v === 'correct') return 'correct';
          if (acc === 'present') return 'present';
          if (v === 'present') return 'present';
          return 'absent';
        }, 'absent');
        if (prev === 'correct') return;
        if (prev === 'present' && best !== 'correct') return;
        ks[ch] = best;
      });
      setKeyStates(ks);

      if (newBoardWon.every(Boolean)) {
        setStatus('won');
        showMsg(`🎉 Dordle solved in ${newGuesses.length}/${MAX_ATTEMPTS}!`, 2500);
        setTimeout(() => setShowShare(true), 2000);
      } else if (newGuesses.length >= MAX_ATTEMPTS) {
        setStatus('lost');
        showMsg('Better luck next time! 😔', 3500);
        setTimeout(() => setShowShare(true), 3000);
      }
      setCurrent('');
    } else if (key === '⌫' || key === 'BACKSPACE') {
      setCurrent(c => c.slice(0, -1));
    } else if (/^[A-Z]$/.test(key) && current.length < WORD_LENGTH) {
      setCurrent(c => c + key);
    }
  }, [current, guesses, boardWon, keyStates, status, targets]);

  useEffect(() => {
    function onKey(e) { handleKey(e.key === 'Backspace' ? '⌫' : e.key.toUpperCase()); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleKey]);

  if (loading) return (
    <div className="game-page text-center">
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
      <p className="mt-2 fw-bold">Loading Dordle…</p>
    </div>
  );

  const guessesLeft = MAX_ATTEMPTS - guesses.length;

  return (
    <div className="game-page">
      <PageSEO page="dordle" />
      {showShare && (
        <ShareModal
          shareText={buildShareText(guesses, status)}
          gameUrl="https://wordzingo.com/dordle"
          onClose={() => setShowShare(false)}
          onNewGame={newGame}
        />
      )}
      <div className="container" style={{ maxWidth: 620 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🟩🟩</span>
          <h1>Dordle</h1>
          <p>Solve 2 secret words at once — 7 guesses, each applied to both boards!</p>
        </div>

        {/* Stats strip */}
        <div className="d-flex justify-content-center gap-4 mb-3" style={{ fontSize: '0.85rem' }}>
          <span><strong style={{ color: 'var(--primary)' }}>{guessesLeft}</strong> guess{guessesLeft !== 1 ? 'es' : ''} left</span>
          <span><strong style={{ color: '#059669' }}>{boardWon.filter(Boolean).length}</strong> / 2 solved</span>
        </div>

        {message && (
          <div className="text-center fw-bold py-2 px-3 rounded-3 mb-3"
            style={{ background: '#1e1b4b', color: '#fff', fontSize: '1rem' }}>
            {message}
          </div>
        )}

        {/* Two boards */}
        <div style={{ display: 'flex', gap: 14, marginBottom: '1.5rem' }}>
          {targets.map((target, i) => (
            <MiniBoard
              key={i}
              boardIdx={i}
              target={target}
              guesses={guesses}
              current={current}
              boardWon={boardWon[i]}
              shake={shake}
            />
          ))}
        </div>

        {status === 'lost' && (
          <div className="alert alert-custom mb-3" style={{ background: '#fee2e2', color: '#991b1b' }}>
            <div className="text-center fw-bold mb-2">💀 Better luck next time!</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.4rem 1.4rem' }}>
              {targets.map((t, i) => (
                <span key={i}>
                  Word {i + 1}: <strong style={{ letterSpacing: 2 }}>{t}</strong>{boardWon[i] ? ' ✅' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Keyboard */}
        <div className="wordle-keyboard">
          {KEYBOARD_ROWS.map((row, i) => (
            <div key={i} className="kb-row">
              {row.map(key => (
                <button
                  key={key}
                  className={`kb-key ${key.length > 1 ? 'wide' : ''} ${keyStates[key] || ''}`}
                  onClick={() => handleKey(key)}
                >{key}</button>
              ))}
            </div>
          ))}
        </div>

        {status !== 'playing' && (
          <div className="text-center mt-4 d-flex justify-content-center gap-2 flex-wrap">
            <button className="solve-btn" onClick={newGame}>🔄 New Dordle</button>
            <button className="btn fw-bold"
              style={{ borderRadius: 50, background: '#4f46e5', color: '#fff', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}
              onClick={() => setShowShare(true)}>
              📤 Share Result
            </button>
            <Link to="/wordle" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}>
              🟩 Classic Wordle
            </Link>
            <Link to="/quordle" className="btn fw-bold"
              style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontFamily: 'Nunito,sans-serif', padding: '0.55rem 1.2rem' }}>
              🟩🟩🟩🟩 Quordle
            </Link>
          </div>
        )}

        <SeoContent game="dordle" />
      </div>
    </div>
  );
}
